"use client"

import { Suspense, useState, useRef, useEffect } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { HeadlineHero, RelatedTools, ResultEmailCaptureCard, ToolNextStepCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import {
  LawnSizeForm,
  type LawnSizeMeasurement,
} from "@/components/tools/lawn-size-form"
import { LawnSizeResults } from "@/components/tools/lawn-size-results"
import {
  calculateLawnSize,
  validateLawnSize,
  MAX_LAWN_SIZE_SQ_FT,
  type LawnSizeInput,
  type LawnSizeResult,
} from "@/lib/lawn-size-calculator"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, runWhenPageVisible, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"

// Build the shareable URL for a lawn-size result. Only the non-sensitive area
// is shared. Street addresses and traced outlines remain local to this visit.
function buildLawnShareUrl(lawnSize: number): string {
  return `${window.location.origin}${buildToolUrl('lawn-size-calculator', {
    lawnSize,
  })}`
}

export function buildValidatedLawnSizeResult(
  measurement: LawnSizeMeasurement,
): LawnSizeResult | null {
  if (!validateLawnSize(measurement.totalAreaSqFt).valid) return null
  const input: LawnSizeInput = {
    measurementMethod: measurement.measurementMethod,
    totalAreaSqFt: measurement.totalAreaSqFt,
    totalPerimeterFt: measurement.totalPerimeterFt,
    polygon: measurement.polygon,
    vertexCount: measurement.vertexCount,
  }
  return calculateLawnSize(input)
}

export function LawnSizeCalculatorContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("lawn-size-calculator")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, suppressForSession, usageCount } = useNewsletterPrompt()
  const [lawnResult, setLawnResult] = useState<LawnSizeResult | null>(null)
  // A completed draw that failed validation (over 5 acres, or a sliver under
  // 100 sq ft). The user still did the work and got a number, so this renders
  // a real result frame instead of silently nulling everything.
  const [invalidMeasurement, setInvalidMeasurement] = useState<{
    sqFt: number
    message: string
    tooLarge: boolean
  } | null>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const resultHeadingRef = useRef<HTMLHeadingElement>(null)
  const measurementTrackedRef = useRef(false)
  const restoreTrackedRef = useRef(false)
  // Scroll to the results ONCE, when they first appear. Without this gate the
  // page re-scrolled on every area change (each vertex drag, add, or delete
  // re-fires onAreaCalculated), yanking the user down and away from the map
  // while they were still drawing or trying to clear.
  const hasScrolledToResultRef = useRef(false)
  // While the map's fullscreen layer locks the body (position:fixed), the page
  // cannot scroll, so a result produced in fullscreen defers its scroll until
  // the layer exits and the lock's cleanup restores the scroll position.
  const isMapFullscreenRef = useRef(false)
  const pendingResultScrollRef = useRef(false)

  const { saveInputs } = useSavedInputs()

  const scrollResultIntoView = (delayMs = 100) => {
    setTimeout(() => {
      if (resultsRef.current) {
        const y = resultsRef.current.getBoundingClientRect().top + window.scrollY - 100
        window.scrollTo({ top: y, behavior: 'smooth' })
        // Move focus with the scroll so keyboard and screen-reader users land
        // on the result instead of staying inside the map controls.
        resultHeadingRef.current?.focus({ preventScroll: true })
      }
    }, delayMs)
  }

  const queueResultScroll = () => {
    if (hasScrolledToResultRef.current) return
    if (isMapFullscreenRef.current) {
      pendingResultScrollRef.current = true
      return
    }
    hasScrolledToResultRef.current = true
    scrollResultIntoView()
  }

  const handleMapFullscreenChange = (fullscreen: boolean) => {
    isMapFullscreenRef.current = fullscreen
    if (!fullscreen && pendingResultScrollRef.current) {
      pendingResultScrollRef.current = false
      hasScrolledToResultRef.current = true
      // The fullscreen cleanup restores the pre-fullscreen scroll position
      // synchronously; wait it out, then bring the result into view.
      scrollResultIntoView(150)
    }
  }

  const handleAreaCalculated = (measurement: LawnSizeMeasurement) => {
    if (measurement.totalAreaSqFt <= 0) {
      setLawnResult(null)
      setInvalidMeasurement(null)
      measurementTrackedRef.current = false
      // Reset so a fresh measurement after clearing scrolls into view again.
      hasScrolledToResultRef.current = false
      pendingResultScrollRef.current = false
      return
    }

    const result = buildValidatedLawnSizeResult(measurement)
    if (!result) {
      const validation = validateLawnSize(measurement.totalAreaSqFt)
      setLawnResult(null)
      setInvalidMeasurement({
        sqFt: Math.round(measurement.totalAreaSqFt),
        message: validation.error ?? 'Check your lawn outline and try again.',
        tooLarge: measurement.totalAreaSqFt > MAX_LAWN_SIZE_SQ_FT,
      })
      // One-shot scroll via the same gate as a valid result; vertex drags on an
      // out-of-range polygon re-fire this path and must not re-yank the page.
      queueResultScroll()
      return
    }

    setLawnResult(result)
    setInvalidMeasurement(null)
    saveInputs({ lawnSize: result.totalAreaSqFt })
    if (!measurementTrackedRef.current) {
      measurementTrackedRef.current = true
      trackUsage('lawn-size-calculator')
      analytics.calculatorUsed("lawn-size-calculator", {
        inputs: {
          measurement_method: result.measurementMethod,
          total_area_sqft: result.totalAreaSqFt,
          restored: false,
          ...(result.measurementMethod === 'map_draw'
            ? { has_address: Boolean(measurement.hasAddress) }
            : {}),
        },
        result: {
          total_area_acres: result.totalAreaAcres,
          mower_deck_recommended: result.mowerDeckSize.recommended,
          spreader_recommended: result.spreaderCapacity.recommended,
          budget_annual: result.budgetEstimate.annual,
        },
        source: "form_submit",
      })
    }
    // Only auto-scroll the first time results appear, not on every live edit.
    queueResultScroll()
  }

  const handleMeasurementModeChange = () => {
    setLawnResult(null)
    setInvalidMeasurement(null)
    measurementTrackedRef.current = false
    hasScrolledToResultRef.current = false
    pendingResultScrollRef.current = false
  }

  // Restore a shared lawn-size result from ?size=N. Recipients won't see the
  // polygon visualization, but they get the same equipment + product
  // recommendations and budget that the original measurer had.
  useEffect(() => {
    const sizeParam = searchParams.get('size')
    if (!sizeParam) return
    const size = parseInt(sizeParam, 10)
    if (!Number.isFinite(size) || size <= 0) return
    const validation = validateLawnSize(size)
    if (!validation.valid) {
      // Out-of-range shares and post-OAuth returns still show the measured
      // number in the same out-of-range frame a fresh draw gets, so the save
      // ask's "keep my measurement" promise holds. Not a `calculator_used`
      // result view: the planning models never ran.
      setInvalidMeasurement({
        sqFt: size,
        message: validation.error ?? 'Check your lawn outline and try again.',
        tooLarge: size > MAX_LAWN_SIZE_SQ_FT,
      })
      return
    }
    const result = calculateLawnSize({
      totalAreaSqFt: size,
      measurementMethod: 'manual_rectangle',
    })
    setLawnResult(result)

    // A restored result is a real result view and belongs in the
    // calculator_used denominator; without this the event undercounts result
    // views on this tool and card impressions exceed result sessions.
    // `restored` keeps it separable from a fresh measurement (same property
    // name and meaning as grass_id_result_viewed), and `url_params` matches
    // how grass-comparison and soil-temperature label URL arrivals.
    // Visibility-gated because this fires on mount: SERP prerenders and
    // background-tab opens would otherwise emit results into pageview-less
    // sessions. The ref keeps a searchParams identity change from re-firing.
    if (restoreTrackedRef.current) return
    return runWhenPageVisible(() => {
      if (restoreTrackedRef.current) return
      restoreTrackedRef.current = true
      analytics.calculatorUsed("lawn-size-calculator", {
        inputs: {
          measurement_method: result.measurementMethod,
          total_area_sqft: result.totalAreaSqFt,
          restored: true,
        },
        result: {
          total_area_acres: result.totalAreaAcres,
          mower_deck_recommended: result.mowerDeckSize.recommended,
          spreader_recommended: result.spreaderCapacity.recommended,
          budget_annual: result.budgetEstimate.annual,
        },
        source: "url_params",
      })
    })
  }, [searchParams])

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        {/* Page-level BreadcrumbList is emitted by ToolArticleSchema in
            layout.tsx; suppress this component's duplicate to avoid two
            BreadcrumbList nodes on the page. */}
        <BreadcrumbComponent
          emitSchema={false}
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Lawn Size Calculator" }
          ]}
        />

        <div className="max-w-4xl mx-auto">
          <div className="mb-4 md:mb-6">
            <HeadlineHero
              eyebrow="Satellite-accurate"
              title="How big is"
              accent="your yard?"
              subtitle="Free lawn size calculator using satellite imagery. Draw your yard, get square footage in seconds, and feed the result into every other calculator on the site."
            />
          </div>

          <div className="mb-8 md:mb-12">
            <LawnSizeForm
              onAreaCalculated={handleAreaCalculated}
              onMeasurementModeChange={handleMeasurementModeChange}
              onMapFullscreenChange={handleMapFullscreenChange}
            />
          </div>

          {!lawnResult && invalidMeasurement && (
            <div className="mt-8" ref={resultsRef}>
              <h2
                ref={resultHeadingRef}
                tabIndex={-1}
                className="text-lg font-semibold text-gray-900 mb-4 focus:outline-hidden"
              >
                Your Lawn Measurement
              </h2>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 sm:p-6">
                <p className="text-sm font-medium text-amber-900">Measured area</p>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-4xl sm:text-5xl font-bold tracking-tight text-amber-900">
                    {invalidMeasurement.sqFt.toLocaleString()}
                  </span>
                  <span className="text-lg font-semibold text-amber-800">sq ft</span>
                </div>
                <p className="text-sm text-amber-800 mt-1">
                  {(invalidMeasurement.sqFt / 43560).toLocaleString(undefined, { maximumFractionDigits: 2 })} acres
                </p>
                <p className="mt-3 text-sm text-amber-800">{invalidMeasurement.message}</p>
              </div>
              {/* The measurement is real even when the planning models stop at 5
                  acres, so a large property still gets the save ask. Copy stays
                  measurement-scoped: no plan, no calendar promise. */}
              {invalidMeasurement.tooLarge && (
                <div className="mt-6 space-y-6">
                  <ResultEmailCaptureCard
                    source="tool_result_lawn_size"
                    toolDisplayName="lawn measurement"
                    subcopy="Keep your measured square footage and acreage on hand for quotes and planning."
                    successSubcopy="Your lawn tips are on the way. Your measurement stays right here to revisit anytime."
                    accountLead={{
                      // Carries ?size= so the post-OAuth return re-renders this
                      // same frame with the measured number (the restore effect
                      // accepts out-of-range values for display).
                      redirectTo: buildToolUrl("lawn-size-calculator", {
                        lawnSize: invalidMeasurement.sqFt,
                      }),
                      headline: "Save my lawn measurement",
                      subcopy: "Keep your measured square footage and acreage on hand for planning and quotes.",
                      onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_lawn_size"),
                    }}
                    onImpression={suppressForSession}
                    onCaptured={() => {
                      closeNewsletterModal()
                    }}
                  />
                  <ToolNextStepCard
                    toolId="lawn-size-calculator"
                    heading="Now match your lawn size to the right grass plan"
                    tagline="You know the square footage. Snap a photo and get the grass type, then every schedule on this site fits your lawn."
                  />
                </div>
              )}
            </div>
          )}

          {lawnResult && (
            <div className="mt-8" ref={resultsRef}>
              <div className="flex items-center justify-between mb-4">
                <h2
                  ref={resultHeadingRef}
                  tabIndex={-1}
                  className="text-lg font-semibold text-gray-900 focus:outline-hidden"
                >
                  Your Lawn Analysis
                </h2>
                <UnifiedShareButton
                  url={typeof window !== 'undefined'
                    ? buildLawnShareUrl(lawnResult.totalAreaSqFt)
                    : ''}
                  title="Lawn Size Results"
                  trackingId="lawn-size-calculator"
                  text={`${lawnResult.totalAreaSqFt.toLocaleString()} sq ft lawn: equipment and product picks`}
                  size="sm"
                />
              </div>
              <LawnSizeResults
                result={lawnResult}
                emailCapture={
                  <ResultEmailCaptureCard
                    source="tool_result_lawn_size"
                    toolDisplayName="lawn plan"
                    headline={`Email me my ${lawnResult.totalAreaSqFt.toLocaleString()} sq ft lawn plan`}
                    subcopy="Planning estimates for seed, fertilizer, equipment, and a DIY budget, sent to your inbox with the rate assumptions clearly labeled."
                    successSubcopy="Your lawn tips are on the way. Your lawn plan stays right here to revisit anytime."
                    accountLead={{
                      redirectTo: buildToolUrl("lawn-size-calculator", {
                        lawnSize: lawnResult.totalAreaSqFt,
                      }),
                      headline: "Save my lawn measurement and plan",
                      subcopy: "Keep your measurement plus seed, fertilizer, and budget estimates, and track your lawn over time.",
                      onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_lawn_size"),
                    }}
                    onImpression={suppressForSession}
                    onCaptured={() => {
                      closeNewsletterModal()
                    }}
                  />
                }
              />
              {/* Grass-ID handoff. This tool has no grass input at all, so the
                  measurement it just produced can't feed a seeding, fertilizer,
                  or watering rate until the grass is known — always eligible. */}
              <ToolNextStepCard
                toolId="lawn-size-calculator"
                heading="Now match your lawn size to the right grass plan"
                tagline="You know the square footage. Snap a photo and get the grass type, then every schedule on this site fits your lawn."
              />
              {/* Buyer's-guide magnet: sized-lawn visitors are measuring to buy
                  product quantities (2026-07-18 placement audit, Tier 3). */}
              <ProductGuideCta source="lawn_size_calculator_result" variant="result" />
            </div>
          )}

          {/* Products never sit above a tool. The block renders below the
              measurement and its results, from the server shell so the anchors
              land in the served HTML (this island is Suspense-bailed by
              useSearchParams). */}
          {picksSlot && <div className="mb-8">{picksSlot}</div>}

          <RelatedTools
            currentToolId="lawn-size-calculator"
            prefillValues={{
              lawnSize: lawnResult?.totalAreaSqFt ?? (searchParams.get('size') ? Number(searchParams.get('size')) : undefined),
            }}
          />

        </div>
      </Reveal>

      {/* Newsletter signup modal - passive fallback, opens on exit-intent / 85% scroll (see useNewsletterPrompt) */}
      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Lawn Size Calculator"
        usageCount={usageCount}
      />
    </div>
  )
}

export function LawnSizeCalculatorPageContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <>
          <ToolPageLoadingFallback
            toolId="lawn-size-calculator"
            // This fallback is the route's crawlable H1 (the island renders
            // post-hydration): keep the brand term + the question + the "yard"
            // noun the query profile carries.
            headingOverride="Lawn Size Calculator: How Big Is My Yard?"
          />
          {/* The fallback IS the served HTML for this route, so the picks have
              to render here too or no crawl ever sees the anchors. They follow
              the skeleton, never precede the tool. */}
          {picksSlot && (
            <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-8">{picksSlot}</div>
          )}
        </>
      }
    >
      <SearchParamsHydrationGate>
        <LawnSizeCalculatorContent picksSlot={picksSlot} />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
